// Config related store state and actions.

import axiosApiClient from '@/api/axiosApiClient'
import { getSavedState, saveState } from '@/store/helpers'
import i18next from '@/utils/i18next'
import log from '@/utils/log'

const ALL_DELIVERY_COLUMNS = ['status', 'clientRefs', 'pickupAddress', 'pickupAt', 'sender', 'dropoffAddress', 'deliveryBy', 'recipient', 'courier']
const DEFAULT_DELIVERY_COLUMNS = ['status', 'clientRefs', 'deliveryBy', 'recipient', 'courier']

// State
export const state = {
	// Get saved locale, if any.
	locale: getSavedState('config.locale') || 'en-HK',
	reasons: {},
	clients: [],
	deliveryColumns: [],
	locations: [],
	locales: [],
}

// Getters
export const getters = {}

// Mutations
export const mutations = {
	SET_LOCALE(state, newValue) {
		i18next.changeLanguage(newValue) // Change i18next language.
		state.locale = newValue // Update current locale in store.
		saveState('config.locale', newValue) // Save to storage.
		log.changeLocale(newValue) // Change log context.
	},
	SET_REASONS(state, reasons) {
		state.reasons = reasons
	},
	SET_CLIENTS(state, clients) {
		state.clients = clients
	},
	SET_DELIVERY_COLUMNS(state, columns) {
		state.deliveryColumns = columns
	},
	SET_LOCATIONS(state, locations) {
		state.locations = locations
	},
	SET_LOCALES(state, locales) {
		state.locales = locales
	},
}

// Actions
export const actions = {
	init({ state }) {
		i18next.changeLanguage(state.locale) // Init i18next language.
	},

	// Updates current locale.
	changeLocale({ commit }, { newLocale }) {
		commit('SET_LOCALE', newLocale)
	},

	// Fetches failure & success reasons from form-field.
	async fetchReasons({ commit }) {
		try {
			// Get all form-fields with tag starting with REASONS_SUCCESS and REASONS_FAILURE 
			const { data: failureData } = await axiosApiClient.get('form-fields?tag=REASONS_FAILURE*')
			const { data: successData } = await axiosApiClient.get('form-fields?tag=REASONS_SUCCESS*')
			const reasons = [...failureData, ...successData].reduce((acc, formField) => {
				const clientId = formField.clientId ? formField.clientId : 'COMMON'
				formField.options.forEach(i => {
					const { tag, translations } = i
					if (!acc[clientId]) acc[clientId] = {}
					acc[clientId][tag] = translations
				})
				return acc
			}, {})
			commit('SET_REASONS', reasons)
		}
		catch (err) {
			log.error('Failed to fetch reasons', { category: 'API' }, err.stack)
		}
	},

	// Fetches clients.
	async fetchClients({ commit, dispatch, rootState }) {
		try {
			const { data } = await axiosApiClient.get('clients')
			commit('SET_CLIENTS', data)
			dispatch('placeOrder/changeClient', { clientId: data[0].id }, { root: true })

			const { clientId, roles } = rootState.auth.currentUser // get clientId and roles from auth store
			// As a default, display the original columns:
			let columns = DEFAULT_DELIVERY_COLUMNS
			if (roles.some(role => role === 'ADM' || role === 'OPS')) {
				// For ADM & OPS, display all columns.
				columns = ALL_DELIVERY_COLUMNS
			} else {
				// For CLT, display columns based on config (if any).
				const client = data.find(c => c.id === clientId)
				const { visibleDeliveryProperties } = client.attributes
				if (visibleDeliveryProperties && visibleDeliveryProperties.length) {
					columns = visibleDeliveryProperties
				}
			}
			commit('SET_DELIVERY_COLUMNS', columns)
		}
		catch (err) {
			log.error('Failed to fetch clients', { category: 'API' }, err.stack)
		}
	},

	// Fetches locations.
	async fetchLocations({ commit }) {
		try {
			const { data } = await axiosApiClient.get('locations')
			commit('SET_LOCATIONS', data)
		}
		catch (err) {
			log.error('Failed to fetch locations', { category: 'API' }, err.stack)
		}
	},

	// Fetches locales.
	async fetchLocales({ commit }) {
		try {
			const { data } = await axiosApiClient.get('locales')
			commit('SET_LOCALES', data)
		}
		catch (err) {
			log.error('Failed to fetch locales', { category: 'API' }, err.stack)
		}
	},
}
